import React, { useState, useEffect } from 'react';
import { Trophy, Calendar, MapPin, Users } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { getTournaments } from '../services/tournamentService';
import TournamentCard from './components/TournamentCard';
import TournamentFilters from './components/TournamentFilters';
import RotatingText from '../components/common/RotatingText';

const TournamentsPage = () => {
  const { isDarkMode } = useTheme();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterYear, setFilterYear] = useState('all');
  const [showUpcomingOnly, setShowUpcomingOnly] = useState(false);

  useEffect(() => {
    fetchTournaments();
  }, [filterCategory, filterYear, showUpcomingOnly]);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      
      const filters = {
        status: 'published' // Only show published tournaments to public
      };
      
      if (filterCategory !== 'all') filters.category = filterCategory;
      if (filterYear !== 'all') filters.year = parseInt(filterYear);
      if (showUpcomingOnly) filters.upcoming = true;

      const response = await getTournaments(filters);
      setTournaments(response.data || []);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  // Client-side search filtering
  const filteredTournaments = tournaments.filter(tournament => {
    const matchesSearch = 
      tournament.tournament_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tournament.venue_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tournament.venue_city && tournament.venue_city.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (tournament.description && tournament.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] md:min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="/tournamentsHero.png" 
            alt="Tournaments" 
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-b from-fsgu-gray-900/60 via-fsgu-gray-900/70 to-fsgu-gray-900/90' 
              : 'bg-gradient-to-b from-black/40 via-black/50 to-black/70'
          }`}></div>
        </div>

        {/* Content */}
        <div className="container-custom px-4 relative z-10 text-center">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold font-cursive mb-8 md:mb-12 text-white leading-tight tracking-tight">
  Tournaments
</h1>
            
            {/* Rotating Text Section */}
            <div className="flex flex-wrap items-center justify-center gap-3 text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light mb-8">
              <span className="drop-shadow-lg text-white">Compete in</span>
              <RotatingText
                texts={[
                  'Excellence',
                  'Championships',
                  'Competition',
                  'Glory',
                ]}
                mainClassName={`font-cursive font-normal px-6 py-3 pb-4 rounded-2xl ${
                  isDarkMode
                    ? 'bg-fsgu-green-700/95 text-white'
                    : 'bg-fsgu-green-700 text-white'
                } drop-shadow-2xl`}
                splitLevelClassName="overflow-visible"
                staggerFrom="first"
                initial={{ y: '100%', opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: '-120%', opacity: 0 }}
                staggerDuration={0.03}
                transition={{ type: 'spring', damping: 30, stiffness: 400 }}
                rotationInterval={3000}
              />
            </div>

            <p className="text-lg sm:text-xl md:text-2xl text-white/90 font-light max-w-3xl mx-auto leading-relaxed mb-12">
              Browse and register for Free State Golf Union tournaments
            </p>

            
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 z-10 hidden md:block">
          <div className="animate-bounce">
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="section">
        <div className="container-custom">
          <TournamentFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            filterCategory={filterCategory}
            setFilterCategory={setFilterCategory}
            filterYear={filterYear}
            setFilterYear={setFilterYear}
            showUpcomingOnly={showUpcomingOnly}
            setShowUpcomingOnly={setShowUpcomingOnly}
          />
        </div>
      </section>

      {/* Results */}
      <section className="section pt-0">
        <div className="container-custom">
          {/* Results Count */}
          <div className="mb-6">
            <p className={`text-sm ${
              isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'
            }`}>
              {loading ? 'Loading...' : `${filteredTournaments.length} tournament${filteredTournaments.length !== 1 ? 's' : ''} found`}
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-fsgu-green-500 mb-4"></div>
                <p className={isDarkMode ? 'text-fsgu-silver-400' : 'text-fsgu-silver-600'}>
                  Loading tournaments...
                </p>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!loading && filteredTournaments.length === 0 && (
            <div className={`text-center py-20 rounded-xl border ${
              isDarkMode
                ? 'bg-fsgu-gray-800 border-fsgu-gray-700'
                : 'bg-fsgu-white border-fsgu-silver-200'
            }`}>
              <Trophy className={`w-16 h-16 mx-auto mb-4 ${
                isDarkMode ? 'text-fsgu-silver-600' : 'text-fsgu-silver-400'
              }`} />
              <h3 className={`text-xl font-semibold mb-2 ${
                isDarkMode ? 'text-fsgu-silver-300' : 'text-fsgu-silver-700'
              }`}>
                Coming Soon
              </h3>
              {/* <p className={`${
                isDarkMode ? 'text-fsgu-silver-500' : 'text-fsgu-silver-500'
              }`}>
                Try adjusting your filters or check back later for upcoming events.
              </p> */}
            </div>
          )}

          {/* Tournament Grid */}
          {!loading && filteredTournaments.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredTournaments.map(tournament => (
                <TournamentCard
                  key={tournament.id}
                  tournament={tournament}
                  featured={tournament.is_featured}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TournamentsPage;